import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import * as Sentry from '@sentry/nextjs';
// Hooks
import { useGetMediaPost } from 'hooks/useGetMediaPost';
import { usePersonMentionPageVisitTracking } from 'hooks/usePersonMentionPageVisitTracking';
// Hocks
import withApollo from 'hocs/withApollo';
// Api
import { GET_MEDIA_POST, GET_MEDIA_POSTS } from 'api/mediaPost/queries';
import {
  GetMediaPost,
  GetMediaPost_getMediaPost,
  GetMediaPostVariables,
} from 'api/mediaPost/types/GetMediaPost';
import {
  GetMediaPosts,
  GetMediaPosts_getMediaPosts_entities,
  GetMediaPostsVariables,
} from 'api/mediaPost/types/GetMediaPosts';
import { StoreStatus } from 'api/graphql-global-types';
// Helpers
import {
  generateVideoMetaDescription,
  generateVideoStructuredData,
  getRoundedVideoDurationSeconds,
  getVideoMimeType,
} from 'helpers/structured-data';
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';
import { withPreservedQuery } from 'helpers/mediaStartTime';
import { getMediaImage } from 'helpers/mediaPost';
import { getEnvLink } from 'helpers/routes';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// UI
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import MediaFeedItem from 'components/Athlete/components/MediaFeedItem/MediaFeedItem';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';
import RouteRedirect from 'components/RouteRedirect/RouteRedirect';

type VideoMediaPostPageProps = PageProps & {
  initialMediaFeed?: GetMediaPost_getMediaPost | null;
  initialRelatedPosts?: GetMediaPosts_getMediaPosts_entities[] | null;
};

type MediaPostPageContentProps = {
  initialMediaFeed?: GetMediaPost_getMediaPost | null;
  initialRelatedPosts?: GetMediaPosts_getMediaPosts_entities[] | null;
};

export const MediaPostPageContent = ({
  initialMediaFeed,
  initialRelatedPosts,
}: MediaPostPageContentProps): JSX.Element => {
  usePersonMentionPageVisitTracking();
  const { data } = useGetMediaPost();
  const mediaFeed = data?.getMediaPost || initialMediaFeed;

  if (!mediaFeed || !('video' in mediaFeed)) {
    return <FourDotsLoader />;
  }

  const image = getMediaImage(mediaFeed);

  const metaDescription = generateVideoMetaDescription(
    mediaFeed?.title,
    mediaFeed?.store,
    mediaFeed?.body
  );

  // Extract video metadata for OGP tags
  const video = mediaFeed.video?.url
    ? {
        url: mediaFeed.video.url,
        type: getVideoMimeType(mediaFeed.video.url),
        duration: mediaFeed.video.duration
          ? getRoundedVideoDurationSeconds(mediaFeed.video.duration / 1000)
          : undefined,
      }
    : undefined;

  const canonicalUrl =
    mediaFeed?.store?.slug && mediaFeed?.slug
      ? `${getEnvLink()}/${mediaFeed.store.slug}/social-media-post/video/${
          mediaFeed.slug
        }`
      : null;

  const storeName = mediaFeed?.store?.storeDetails?.storeName || 'MILLIONS';

  // Drop the trailing "| ContentType" from the meta title so it stays ≤65 chars
  // and differs from the H1 (which shows the full title)
  const titleBase = mediaFeed?.title?.includes(' | ')
    ? mediaFeed.title.split(' | ').slice(0, -1).join(' | ')
    : mediaFeed?.title;
  const seoTitle =
    titleBase && `${titleBase} | MILLIONS`.length <= 65
      ? `${titleBase} | MILLIONS`
      : titleBase || 'MILLIONS';

  const imageAlt = mediaFeed?.title
    ? `${mediaFeed.title} – ${storeName}`
    : storeName;

  return (
    <>
      <Head
        title={seoTitle}
        description={metaDescription}
        image={image}
        imageAlt={imageAlt}
        structuredData={generateVideoStructuredData(mediaFeed)}
        canonicalLink={canonicalUrl}
        video={video}
      />
      <AppBannerAndModal
        when={!!mediaFeed}
        variant="profile"
        username={mediaFeed?.store?.slug ?? undefined}
        profileImageUrl={mediaFeed?.store?.storeDetails?.avatarURL ?? undefined}
      />
      <Header />

      <Main withoutSidesPadding>
        <MediaFeedItem
          mediaFeed={mediaFeed}
          initialRelatedPosts={initialRelatedPosts}
        />
      </Main>
      <Footer />
    </>
  );
};

const MediaPostPage: NextPage<VideoMediaPostPageProps> = ({
  isNotFound,
  redirectTo,
  initialMediaFeed,
  initialRelatedPosts,
}) => {
  if (redirectTo) {
    return <RouteRedirect to={redirectTo} />;
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  return (
    <MediaPostPageContent
      initialMediaFeed={initialMediaFeed}
      initialRelatedPosts={initialRelatedPosts}
    />
  );
};

MediaPostPage.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<VideoMediaPostPageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);
  const mediaPostSlug = getCleanQueryParam(ctx.query.mediaPostSlug);

  if (!ctx.apolloClient || !athleteSlug || !mediaPostSlug) {
    return {};
  }

  try {
    // Fire both queries in parallel — both can run from URL params alone
    // (the related-posts list keys on storeSlug). Cuts SSR roundtrip time
    // ~in half vs. sequential. Related-posts errors are non-fatal: the main
    // post still renders and the client will refetch on hydration.
    const postPromise = ctx.apolloClient.query<
      GetMediaPost,
      GetMediaPostVariables
    >({
      query: GET_MEDIA_POST,
      variables: {
        input: {
          slug: mediaPostSlug,
          storeSlug: athleteSlug,
        },
      },
    });

    const relatedPromise = ctx.apolloClient
      .query<GetMediaPosts, GetMediaPostsVariables>({
        query: GET_MEDIA_POSTS,
        variables: {
          input: {
            storeSlug: athleteSlug,
            storeStatuses: [StoreStatus.Active, StoreStatus.Pending],
          },
        },
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: {
            section: 'ssr-prefetch',
            page: 'social-media-post/video/[mediaPostSlug]',
            query: 'GET_MEDIA_POSTS',
          },
        });
        return null;
      });

    const [postResult, relatedResult] = await Promise.all([
      postPromise,
      relatedPromise,
    ]);

    const mediaFeed = postResult.data?.getMediaPost;
    const initialRelatedPosts =
      relatedResult?.data?.getMediaPosts?.entities ?? null;

    if (!mediaFeed) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }

    if (!('video' in mediaFeed) && mediaFeed.store?.slug && mediaFeed.slug) {
      const redirectTo = withPreservedQuery(
        `/${mediaFeed.store.slug}/social-media-post/${mediaFeed.slug}`,
        ctx.query
      );

      if (ctx.res) {
        ctx.res.writeHead(301, { Location: redirectTo });
        ctx.res.end();
        return { redirectTo };
      }

      return { redirectTo };
    }

    return { initialMediaFeed: mediaFeed, initialRelatedPosts };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssr', page: 'social-media-post/video/[mediaPostSlug]' },
    });
    markPageAsNotFound(ctx);
    return { isNotFound: true };
  }

  return {};
};

// `getDataFromTree` is required so `next-with-apollo` extracts the cache
// from getInitialProps and serializes it into `__NEXT_DATA__` for hydration.
// We pre-warm the cache in getInitialProps with the two critical queries
// (parallel), so the walker just traverses the tree finding cache hits — no
// extra network calls.
export default withApollo(MediaPostPage, { getDataFromTree });
